import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Add, Close } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';

import { STOItemStatusEnum } from 'commons/enums';
import S from 'commons/styles/assign-sto-item-page.styles';
import { STOItem } from 'commons/types';
import {
  actionAddAssignedSTOPickItem,
  actionRemoveAssignedSTOPickItem,
  actionSetAssignedSTOPickItem,
  actionStartSTOPickItem,
  getSTOAssignedPickItems,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { RemoveSTOPickItemDialog } from './components';

export const AssignSTOPickItemPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { assignedSTOItems } = useSelector(
    (state: RootReducerInterface) => state.stoPickItem,
  );
  const [openRemoveModal, setOpenRemoveModal] = useState<boolean>(false);
  const [removedSTOItem, setRemovedSTOItem] = useState<STOItem | null>(null);

  useEffect(() => {
    dispatch(getSTOAssignedPickItems());
  }, [dispatch]);

  const assignSTOItem = (): void => {
    if (assignedSTOItems.length === 0) {
      dispatch(actionSetAssignedSTOPickItem());
    } else {
      dispatch(actionAddAssignedSTOPickItem());
    }
  };

  const startPicking = async () => {
    dispatch(actionStartSTOPickItem({ navigate, items: assignedSTOItems }));
  };

  const onRemoveModalOpen = (item: STOItem): void => {
    setOpenRemoveModal(true);
    setRemovedSTOItem(item);
  };
  const onRemoveModalClose = (): void => setOpenRemoveModal(false);
  const removeSTO = (item: STOItem): void => {
    dispatch(
      actionRemoveAssignedSTOPickItem({
        stoNumber: item.sto_number,
        skuCode: item.sku_code,
      }),
    );
    setOpenRemoveModal(false);
  };

  const validateAssignment = (): boolean => {
    if (assignedSTOItems.length === 0) return false;
    return true;
  };

  const renderSkuList = (items: STOItem[]) =>
    items.map((item, idx) => (
      <S.GridWrapper container>
        <Grid
          item
          xs={1}
          style={{
            display: item.status === STOItemStatusEnum.QUEUE ? 'flex' : 'none',
            justifyContent: 'center',
            alignContent: 'center',
          }}
        >
          <IconButton
            onClick={() => onRemoveModalOpen(item)}
            data-testid={`BtnAssignRemoveSTOPickItem-${idx + 1}`}
          >
            <Close fontSize="small" />
          </IconButton>
        </Grid>

        <Grid item xs={item.status === STOItemStatusEnum.QUEUE ? 11 : 12}>
          <S.SkuNumberGridWrapper container>
            <S.SkuInfoGrid item xs={5} lg={1.25}>
              <p data-testid={`TextAssignSTOPickItem-${idx + 1}`}>
                SKU {item.sku_code}
              </p>
            </S.SkuInfoGrid>
            <S.SkuInfoGrid className="sto-number" item xs={7}>
              <S.STONumberInfoGrid
                data-testid="TextAssignPickItemSTONumber"
                style={{ textTransform: 'uppercase' }}
              >
                {item.sto_number}
              </S.STONumberInfoGrid>
            </S.SkuInfoGrid>
            <S.SkuInfoGrid
              item
              xs={6}
              data-testid={`TextAssignQTYPick-${idx + 1}`}
            >
              <S.AdditionalInfoGrid>
                QTY Picked:{' '}
                {item.pick_actual.reduce(
                  (total, _item) => _item.quantity + total,
                  0,
                )}
              </S.AdditionalInfoGrid>
            </S.SkuInfoGrid>
            <S.SkuInfoGrid
              item
              xs={6}
              style={{ textAlign: 'right' }}
              data-testid={`TextAssignPalletPosition-${idx + 1}`}
            >
              <S.AdditionalInfoGrid>
                Basket:{' '}
                {item.pick_actual
                  .map((_item) => _item.basket)
                  .filter(
                    (_basket, pos, _basketArr) =>
                      _basketArr.indexOf(_basket) === pos,
                  )
                  .join(', ') || '-'}
              </S.AdditionalInfoGrid>
            </S.SkuInfoGrid>
          </S.SkuNumberGridWrapper>
        </Grid>
      </S.GridWrapper>
    ));

  return (
    <div>
      {/* Content Section */}
      <S.ContentWrapper>
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="TextAssignSTOPickItem">
            Assign SKU
          </S.Subtitle>
        </S.SubtitleWrapper>

        {renderSkuList(assignedSTOItems)}

        <S.AddSTOItemButtonWrapper>
          <S.SecondaryButton
            onClick={assignSTOItem}
            data-testid="BtnAssignSTOPickItem"
          >
            <Add />{' '}
            {assignedSTOItems.length > 0 ? 'ASSIGN ANOTHER SKU' : 'ASSIGN SKU'}
          </S.SecondaryButton>
        </S.AddSTOItemButtonWrapper>
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer Section */}
      <S.FooterWrapper>
        <S.PrimaryButton
          disabled={!validateAssignment()}
          onClick={startPicking}
          data-testid="BtnAssignStartPickingSTO"
        >
          START PICKING
        </S.PrimaryButton>
      </S.FooterWrapper>

      {/* Modal */}
      {openRemoveModal && (
        <RemoveSTOPickItemDialog
          item={removedSTOItem}
          onClose={onRemoveModalClose}
          onConfirm={removeSTO}
          openModal={openRemoveModal}
        />
      )}
    </div>
  );
};
